(() => {
  if (window.__collectionFilterSheetInitialized) return;
  window.__collectionFilterSheetInitialized = true;

  const getSectionInnerHtml = (html, id) =>
    new DOMParser().parseFromString(html, 'text/html').getElementById(id)?.innerHTML ?? '';

  class CollectionFilterSheet {
    constructor(dialog) {
      this.dialog = dialog;
      this.sectionId = dialog.dataset.sectionId;
      this.panel = dialog.querySelector('[data-element="sheet-panel"]');
      this.body = dialog.querySelector('[data-element="sheet-body"]');
      this.applyButton = dialog.querySelector('[data-action="apply-filters"]');
      this.resetButton = dialog.querySelector('[data-action="reset-filters"]');
      this.triggers = [];
      this.activeTrigger = null;
      this.viewProductsTemplate = dialog.dataset.viewProductsTemplate || 'View __COUNT__ products';
      this.pendingParams = new URLSearchParams(window.location.search);
      this.previewController = null;
      this.historyActive = false;
      this.ignorePopState = false;
      this.previousFocus = null;
      this.hideUnavailable = dialog.dataset.hideUnavailable === 'true';
      this.pinAvailabilityFilter();

      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-action="open-filter-sheet"]');
        if (!trigger) return;

        event.preventDefault();
        this.open(trigger);
      });

      this.dialog.querySelector('[data-action="close-sheet"]')?.addEventListener('click', () => {
        this.closeFromUser();
      });

      this.resetButton?.addEventListener('click', () => {
        this.resetFilters();
      });

      this.applyButton?.addEventListener('click', () => {
        this.applyFilters();
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeFromUser();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.closeFromUser();
        }
      });

      this.body?.addEventListener('change', (event) => this.handleInputChange(event));
      this.body?.addEventListener('click', (event) => this.handleClick(event), true);
      document.addEventListener('click', (event) => this.handleChipClick(event));

      this.panel?.addEventListener('keydown', (event) => this.handleKeydown(event));

      window.addEventListener('popstate', () => this.handlePopState());

      document.addEventListener(
        'click',
        (event) => {
          if (!document.body.classList.contains('collection-filter-sheet-open')) return;
          const blocked = event.target.closest('[popovertarget], [data-element="open-button"][popovertarget]');
          if (blocked) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    }

    syncTriggerExpanded(isExpanded) {
      document.querySelectorAll('[data-action="open-filter-sheet"]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    }

    open(trigger) {
      this.activeTrigger = trigger || document.getElementById('facet-filter-trigger');

      if (!this.dialog.open) {
        if (document.body.classList.contains('collection-filter-sheet-open')) return;

        this.previousFocus = document.activeElement;
        this.pendingParams = new URLSearchParams(window.location.search);
        this.pinAvailabilityFilter();
        this.applying = false;
        this.syncUiFromParams();
        this.updateApplyLabel(this.readCountFromDom());

        this.historyActive = true;
        history.pushState({ collectionFilterSheet: true }, '');

        document.body.classList.add('collection-filter-sheet-open');
        this.dialog.showModal();
        this.syncTriggerExpanded(true);
        if (this.body) this.body.scrollTop = 0;
        this.trapFocus();
        this.prepareSection(trigger);
        this.schedulePreviewCount();
      } else {
        this.prepareSection(trigger);
      }
    }

    prepareSection() {
      if (!this.body) return;

      const sortDetails = this.body.querySelector('.collection-filter-sheet__sort details');
      if (sortDetails instanceof HTMLDetailsElement) {
        sortDetails.open = false;
      }

      this.body.scrollTop = 0;
    }

    scrollToSection(trigger) {
      this.prepareSection(trigger);
    }

    closeFromUser() {
      if (!this.dialog.open) return;

      if (this.historyActive) {
        this.ignorePopState = true;
        history.back();
      }

      this.closeDialog(false);
    }

    closeDialog(restoreFocus = true) {
      if (!this.dialog.open) return;

      this.dialog.close();
      document.body.classList.remove('collection-filter-sheet-open');
      this.syncTriggerExpanded(false);
      this.historyActive = false;
      this.previewController?.abort();
      this.previewController = null;

      if (restoreFocus) {
        (this.activeTrigger || this.previousFocus)?.focus?.();
      }

      this.activeTrigger = null;
    }

    handlePopState() {
      if (this.ignorePopState) {
        this.ignorePopState = false;
        return;
      }

      if (this.dialog.open) {
        this.closeDialog(true);
        return;
      }

      this.pendingParams = new URLSearchParams(window.location.search);
      this.pinAvailabilityFilter();
      this.fetchSection(window.location.search.replace(/^\?/, ''));
    }

    handleKeydown(event) {
      if (event.key === 'Tab') {
        this.trapFocus(event);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeFromUser();
      }
    }

    trapFocus(event) {
      const focusables = this.getFocusableElements();
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event?.key === 'Tab') {
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      if (!this.panel.contains(document.activeElement)) {
        first.focus();
      }
    }

    getFocusableElements() {
      return Array.from(
        this.panel.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]):not(.sr-only), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        if (element.classList.contains('sr-only')) return false;
        if (element.closest('.sr-only')) return false;
        if (element.closest('details:not([open])') && !element.closest('summary')) return false;
        return element.offsetParent !== null || element === this.dialog;
      });
    }

    handleInputChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.type === 'checkbox') {
        this.updateCheckboxParam(target);
      }

      if (target.type === 'radio' && (target.name === 'sort_by' || target.name === 'sheet_sort_by')) {
        this.selectSort(target);
        this.commitSort();
        return;
      }

      const priceFilter = target.type === 'number' ? target.closest('[data-module="price-filter"]') : null;
      if (priceFilter) {
        this.updatePriceParams(priceFilter);
        this.syncPriceSummary(priceFilter);
      }

      this.schedulePreviewCount();
    }

    selectSort(radio) {
      if (!(radio instanceof HTMLInputElement)) return;

      radio.checked = true;
      this.body?.querySelectorAll('input[type="radio"][name="sheet_sort_by"], input[type="radio"][name="sort_by"]').forEach((input) => {
        if (input !== radio) input.checked = false;
      });
      this.pendingParams.set('sort_by', radio.value);

      const optionLabel = radio.closest('label')?.innerText?.trim();
      const summaryValue = this.body?.querySelector(
        '.collection-filter-sheet__sort [data-element="current-value"]'
      );
      if (summaryValue && optionLabel) summaryValue.textContent = optionLabel;
    }

    handleClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const sortOption = target.closest('.collection-filter-sheet__sort .selector-filter__option');
      if (sortOption) {
        event.preventDefault();
        event.stopPropagation();
        const details = sortOption.closest('details');
        const radio = sortOption.querySelector('input[type="radio"]');
        if (radio instanceof HTMLInputElement) {
          this.selectSort(radio);
          this.commitSort();
        }
        if (details instanceof HTMLDetailsElement) {
          details.open = true;
        }
        return;
      }

      const resetLink = target.closest(
        '.selector-filter a[href], .price-filter a[data-element="reset-filter"], .price-filter a[href]'
      );
      if (resetLink instanceof HTMLAnchorElement && this.body.contains(resetLink)) {
        event.preventDefault();
        event.stopPropagation();
        this.applyResetLink(resetLink.href);
        this.schedulePreviewCount();
        return;
      }

      const clearPrice = target.closest('[data-element="clear-price"]');
      if (clearPrice) {
        event.preventDefault();
        event.stopPropagation();
        const priceFilter = clearPrice.closest('[data-module="price-filter"]');
        if (priceFilter) {
          priceFilter.querySelector('[data-element="price-min"]').value = '';
          priceFilter.querySelector('[data-element="price-max"]').value = '';
          this.updatePriceParams(priceFilter);
          this.syncPriceSummary(priceFilter);
          this.schedulePreviewCount();
        }
        return;
      }

      const applyPrice = target.closest('[data-element="apply-price"]');
      if (applyPrice) {
        event.preventDefault();
        event.stopPropagation();
        const priceFilter = applyPrice.closest('[data-module="price-filter"]');
        if (priceFilter) {
          this.updatePriceParams(priceFilter);
          this.syncPriceSummary(priceFilter);
          this.collapsePriceFilter(priceFilter);
          clearTimeout(this.previewTimeout);
          this.previewCount();
        }
      }
    }

    syncPriceSummary(priceFilter) {
      const target = priceFilter.querySelector('[data-element="current-value"]');
      if (!target) return;

      const min = priceFilter.querySelector('[data-element="price-min"]')?.value.trim() || '';
      const max = priceFilter.querySelector('[data-element="price-max"]')?.value.trim() || '';
      const templates = priceFilter.dataset;

      let label = '';
      if (min && max) {
        label = (templates.valueTemplate || '').replace('__MIN__', min).replace('__MAX__', max);
      } else if (min) {
        label = (templates.valueFromTemplate || '').replace('__MIN__', min);
      } else if (max) {
        label = (templates.valueToTemplate || '').replace('__MAX__', max);
      }

      target.textContent = label;
      priceFilter.querySelector('.selector-filter__count')?.toggleAttribute('hidden', !label);
    }

    collapsePriceFilter(priceFilter) {
      const details = priceFilter instanceof HTMLDetailsElement ? priceFilter : priceFilter.closest('details');
      if (!details?.open) return;

      details.open = false;
      details.querySelector('summary')?.focus();
    }

    updateCheckboxParam(checkbox) {
      const container = checkbox.closest('[data-module="selector"]');
      const paramName = container?.getAttribute('data-param-name');
      if (!paramName) return;

      if (checkbox.checked) {
        if (!this.pendingParams.getAll(paramName).includes(checkbox.value)) {
          this.pendingParams.append(paramName, checkbox.value);
        }
      } else {
        const remaining = this.pendingParams.getAll(paramName).filter((value) => value !== checkbox.value);
        this.pendingParams.delete(paramName);
        remaining.forEach((value) => this.pendingParams.append(paramName, value));
      }
      this.syncSelectorCounts();
    }

    syncSelectorCounts() {
      this.body.querySelectorAll('[data-module="selector"]').forEach((selector) => {
        const paramName = selector.getAttribute('data-param-name');
        if (!paramName?.startsWith('filter.')) return;

        const label = selector.querySelector('.selector-filter__summary-label');
        if (!label) return;

        const count = new Set(this.pendingParams.getAll(paramName)).size;
        let badge = label.querySelector('.selector-filter__count');
        if (!count) {
          badge?.remove();
          return;
        }

        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'selector-filter__count';
          label.append(badge);
        }
        badge.textContent = String(count);
      });
    }

    updatePriceParams(priceFilter) {
      const minInput = priceFilter.querySelector('[data-element="price-min"]');
      const maxInput = priceFilter.querySelector('[data-element="price-max"]');
      const minValue = minInput?.value.trim();
      const maxValue = maxInput?.value.trim();

      this.pendingParams.delete('filter.v.price.gte');
      this.pendingParams.delete('filter.v.price.lte');

      if (minValue) this.pendingParams.set('filter.v.price.gte', minValue);
      if (maxValue) this.pendingParams.set('filter.v.price.lte', maxValue);
    }

    handleChipClick(event) {
      const chip = event.target.closest('[data-active-filter-chip], [data-active-filter-reset]');
      if (!(chip instanceof HTMLAnchorElement)) return;
      if (this.dialog.open) return;

      event.preventDefault();
      const url = new URL(chip.href, window.location.origin);
      this.pendingParams = new URLSearchParams(url.search);
      this.pinAvailabilityFilter();
      const search = this.pendingParams.toString();
      const nextUrl = search ? `${url.pathname}?${search}` : url.pathname;

      history.pushState({ searchParams: search }, '', nextUrl);
      this.fetchSection(search)
        .then(() => {
          const chips = document.querySelector('[data-active-filter-chips]');
          const count = document.querySelector('[id^="product-count-"]');
          (chips || count)?.focus?.();
        })
        .catch(() => {
          window.location.assign(nextUrl);
        });
    }

    applyResetLink(href) {
      const url = new URL(href, window.location.origin);
      this.pendingParams = new URLSearchParams(url.search);
      this.pinAvailabilityFilter();
      this.syncUiFromParams();
    }

    pinAvailabilityFilter() {
      if (this.hideUnavailable) this.pendingParams.set('filter.v.availability', '1');
    }

    resetFilters() {
      for (const key of [...this.pendingParams.keys()]) {
        if (key.startsWith('filter.') || key === 'page') this.pendingParams.delete(key);
      }
      this.pinAvailabilityFilter();
      this.syncUiFromParams();
      this.schedulePreviewCount();
    }

    syncUiFromParams() {
      this.body.querySelectorAll('[data-module="selector"] input[type="checkbox"]').forEach((checkbox) => {
        if (!(checkbox instanceof HTMLInputElement)) return;
        const paramName = checkbox.closest('[data-module="selector"]')?.getAttribute('data-param-name');
        checkbox.checked = paramName ? this.pendingParams.getAll(paramName).includes(checkbox.value) : false;
      });
      this.syncSelectorCounts();

      this.body.querySelectorAll('input[type="radio"][name="sort_by"], input[type="radio"][name="sheet_sort_by"]').forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        radio.checked = radio.value === (this.pendingParams.get('sort_by') || '');
      });

      this.body.querySelectorAll('[data-module="price-filter"]').forEach((priceFilter) => {
        const minInput = priceFilter.querySelector('[data-element="price-min"]');
        const maxInput = priceFilter.querySelector('[data-element="price-max"]');
        if (minInput) minInput.value = this.pendingParams.get('filter.v.price.gte') || '';
        if (maxInput) maxInput.value = this.pendingParams.get('filter.v.price.lte') || '';
        this.syncPriceSummary(priceFilter);
      });
    }

    commitSort() {
      this.pendingParams.delete('page');
      const search = this.pendingParams.toString();
      const nextUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;

      history.replaceState(
        { ...(history.state || {}), collectionFilterSheet: this.historyActive, searchParams: search },
        '',
        nextUrl
      );

      this.fetchSection(search, { keepSheet: true });
      this.schedulePreviewCount();
    }

    applyFilters() {
      if (this.applying) return;
      this.applying = true;

      this.pendingParams.delete('page');
      const search = this.pendingParams.toString();
      const nextUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;

      if (this.historyActive) {
        history.replaceState({ searchParams: search }, '', nextUrl);
        this.historyActive = false;
      } else {
        history.pushState({ searchParams: search }, '', nextUrl);
      }

      this.fetchSection(search);
      this.closeDialog(true);
    }

    schedulePreviewCount() {
      clearTimeout(this.previewTimeout);
      this.previewTimeout = window.setTimeout(() => this.previewCount(), 300);
    }

    async previewCount() {
      this.previewController?.abort();
      const controller = this.previewController = new AbortController();
      let timeout;

      const search = this.pendingParams.toString();
      const url = `${window.location.origin}${window.location.pathname}?section_id=${this.sectionId}${search ? `&${search}` : ''}`;

      try {
        const text = await Promise.race([
          fetch(url, { signal: controller.signal }).then(response => {
            if (!response.ok) throw new Error('Filter count unavailable');
            return response.text();
          }),
          new Promise((_, reject) => {
            timeout = setTimeout(() => { controller.abort(); reject(new Error('Filter count timeout')); }, 8000);
          }),
        ]);
        if (controller !== this.previewController || search !== this.pendingParams.toString()) return;
        const countMarkup = getSectionInnerHtml(text, `product-count-${this.sectionId}`);
        if (!countMarkup && text.includes('data-search-results-summary')) {
          this.updateApplyLabel(0);
          return;
        }
        const parsed = new DOMParser().parseFromString(`<div>${countMarkup}</div>`, 'text/html');
        const countText = parsed.body.textContent?.replace(/\s+/g, ' ').trim() || '';
        const match = countText.match(/(\d[\d.,]*)/);
        this.updateApplyLabel(match ? Number(match[1].replace(/\./g, '').replace(/,/g, '')) : this.readCountFromDom());
      } catch (error) {
        if (error.name !== 'AbortError') {
          window.__ottoReportUIError?.('filters');
        }
      } finally { clearTimeout(timeout); }
    }

    readCountFromDom() {
      const countEl = document.getElementById(`product-count-${this.sectionId}`);
      const text = countEl?.textContent?.replace(/\s+/g, ' ').trim() || '';
      const match = text.match(/(\d[\d.,]*)/);
      return match ? Number(match[1].replace(/\./g, '').replace(/,/g, '')) : 0;
    }

    updateApplyLabel(count) {
      if (!this.applyButton) return;
      const safeCount = Number.isFinite(count) ? count : 0;
      this.applyButton.textContent = this.viewProductsTemplate.replace('__COUNT__', safeCount.toLocaleString(document.documentElement.lang || 'da'));
    }

    async fetchSection(search, { keepSheet = false } = {}) {
      const revision = this.sectionRevision = (this.sectionRevision || 0) + 1;
      this.sectionController?.abort();
      const controller = this.sectionController = new AbortController();
      let timeout;
      const url = `${window.location.origin}${window.location.pathname}?section_id=${this.sectionId}${search ? `&${search}` : ''}`;
      try {
      const text = await Promise.race([
        fetch(url, { signal: controller.signal, cache: 'no-store' }).then(response => {
          if (!response.ok) throw new Error('Filter results unavailable');
          return response.text();
        }),
        new Promise((_, reject) => {
          timeout = setTimeout(() => { controller.abort(); reject(new Error('Filter timeout')); }, 10000);
        }),
      ]);
      if (revision !== this.sectionRevision) return false;
      const parsed = new DOMParser().parseFromString(text, 'text/html');

      const collection = document.getElementById('collection-with-facets');
      const nextCollection = parsed.getElementById('collection-with-facets');
      if (!collection || !nextCollection) throw new Error('Missing filter results');
      if (collection && nextCollection) {
        collection.innerHTML = nextCollection.innerHTML;
      }

      document.querySelectorAll('[id^="product-count-"]').forEach((productCount) => {
        const nextCount =
          parsed.getElementById(productCount.id) || parsed.querySelector('[id^="product-count-"]');
        if (nextCount) productCount.innerHTML = nextCount.innerHTML;
      });

      const summary = document.querySelector('[data-search-results-summary]');
      const nextSummary = parsed.querySelector('[data-search-results-summary]');
      if (summary && nextSummary) summary.innerHTML = nextSummary.innerHTML;

      document.querySelectorAll('[id^="facet-group-"]').forEach((facetGroup) => {
        if (keepSheet && this.dialog.contains(facetGroup)) return;
        const nextGroup = parsed.getElementById(facetGroup.id);
        if (nextGroup) facetGroup.innerHTML = nextGroup.innerHTML;
      });

      const trigger = document.getElementById('facet-filter-trigger');
      const nextTrigger = parsed.getElementById('facet-filter-trigger');
      if (trigger && nextTrigger) trigger.innerHTML = nextTrigger.innerHTML;

      const sheetBody = document.querySelector('#collection-filter-sheet [data-element="sheet-body"]');
      const fetchedSheetBody = parsed.querySelector('#collection-filter-sheet [data-element="sheet-body"]');

      if (!keepSheet && sheetBody && fetchedSheetBody) {
        sheetBody.innerHTML = fetchedSheetBody.innerHTML;
        this.syncUiFromParams();
      }

      if (keepSheet) {
        const sortDetails = this.body?.querySelector('.collection-filter-sheet__sort details');
        if (sortDetails instanceof HTMLDetailsElement) sortDetails.open = true;
      }

      document.dispatchEvent(new CustomEvent('collection:updated'));
      return true;
      } catch (error) {
        if (revision !== this.sectionRevision) return false;
        window.__ottoReportUIError?.('filters');
        const target = `${window.location.pathname}${search ? `?${search}` : ''}`;
        window.location.assign(target);
        return false;
      } finally {
        clearTimeout(timeout);
        if (revision === this.sectionRevision) this.applying = false;
      }
    }
  }

  const init = () => {
    const dialog = document.getElementById('collection-filter-sheet');
    if (!dialog) return;
    new CollectionFilterSheet(dialog);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
